import type {
    ConvertQuotationToInvoiceBody,
    CreateDocumentDTO,
    CreateDocumentPaymentInput,
    UpdateDocumentPaymentInput,
    UpdateIssuerSettingsInput,
} from "@/modules/quotation-invoice/domain/types";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { quotationInvoiceApi } from "./client";

export * from "./my-documents-queries";

export type HistoryListQuery = {
  offset?: number;
  limit?: number;
  dateFrom?: string;
  dateTo?: string;
  documentType?: string;
  action?: string;
  q?: string;
};

export const quotationInvoiceKeys = {
  all: ["quotation-invoice"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) =>
    ["quotation-invoice", "list", q] as const,
  detail: (id: string) => ["quotation-invoice", "detail", id] as const,
  issuerSettings: () => ["quotation-invoice", "issuer-settings"] as const,
  summary: () => ["quotation-invoice", "summary"] as const,
  customers: (params: {
    q?: string;
    limit?: number;
    skip?: number;
    includeInactive?: boolean;
    categoryId?: string;
  }) => ["quotation-invoice", "customers", params] as const,
  customerCategories: (params: { includeInactive?: boolean }) =>
    ["quotation-invoice", "customer-categories", params] as const,
  history: (params: HistoryListQuery) =>
    ["quotation-invoice", "history", params] as const,
  historySummary: () => ["quotation-invoice", "history", "summary"] as const,
  documentTypes: (params: { limit?: number; includeInactive?: boolean }) =>
    ["quotation-invoice", "document-types", params] as const,
  catalogProducts: (params: {
    q?: string;
    categoryId?: string;
    limit?: number;
    offset?: number;
  }) => ["quotation-invoice", "catalog", "products", params] as const,
  catalogRawMaterials: (params: {
    q?: string;
    categoryId?: string;
    limit?: number;
    offset?: number;
  }) => ["quotation-invoice", "catalog", "raw-materials", params] as const,
  catalogServices: (params: {
    q?: string;
    categoryId?: string;
    limit?: number;
    offset?: number;
  }) => ["quotation-invoice", "catalog", "services", params] as const,
  catalogCategories: () =>
    ["quotation-invoice", "catalog", "categories"] as const,
  headOfficeBranches: () =>
    ["quotation-invoice", "catalog", "head-office-branches"] as const,
  productAvailability: (params: {
    productId: string;
    branchId: string;
    excludeQuotationId?: string;
  }) => ["quotation-invoice", "availability", params] as const,
};

export function useQuotationInvoiceDocumentsQuery(
  query: Partial<OffsetPageQueryDTO> = {},
) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: quotationInvoiceKeys.list(q),
    queryFn: () => quotationInvoiceApi.list(q),
  });
}

export function useQuotationInvoiceDocumentQuery(id: string) {
  return useQuery({
    queryKey: quotationInvoiceKeys.detail(id),
    queryFn: () => quotationInvoiceApi.get(id),
    enabled: !!id,
  });
}

export const quotationInvoiceDetailKeys = {
  detail: (id: string) => ["quotation-invoice", "detail", id] as const,
};

export function useQuotationInvoiceDetailQuery(id: string) {
  return useQuery({
    queryKey: quotationInvoiceDetailKeys.detail(id),
    queryFn: () => quotationInvoiceApi.getDetail(id),
    enabled: !!id,
  });
}

export function useQuotationInvoiceIssuerSettingsQuery() {
  return useQuery({
    queryKey: quotationInvoiceKeys.issuerSettings(),
    queryFn: () => quotationInvoiceApi.getIssuerSettings(),
    staleTime: 60_000,
  });
}

export function useUpdateQuotationInvoiceIssuerSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateIssuerSettingsInput) =>
      quotationInvoiceApi.updateIssuerSettings(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: quotationInvoiceKeys.issuerSettings(),
      });
      toast.success("ບັນທຶກການຕັ້ງຄ່າສຳເລັດ");
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : "ບັນທຶກລົ້ມເຫຼວ");
    },
  });
}

export function useQuotationInvoiceSummaryQuery() {
  return useQuery({
    queryKey: quotationInvoiceKeys.summary(),
    queryFn: () => quotationInvoiceApi.getSummary(),
  });
}

export function useQuotationInvoiceDocumentTypesQuery(
  params: { limit?: number; includeInactive?: boolean } = {},
) {
  return useQuery({
    queryKey: quotationInvoiceKeys.documentTypes(params),
    queryFn: () => quotationInvoiceApi.getDocumentTypes(params),
  });
}

export function useQuotationInvoiceCustomersQuery(
  params: {
    q?: string;
    limit?: number;
    skip?: number;
    includeInactive?: boolean;
    categoryId?: string;
  } = {},
) {
  return useQuery({
    queryKey: quotationInvoiceKeys.customers(params),
    queryFn: () => quotationInvoiceApi.getCustomers(params),
  });
}

export function useQuotationInvoiceCustomerCategoriesQuery(
  params: { includeInactive?: boolean } = {},
) {
  return useQuery({
    queryKey: quotationInvoiceKeys.customerCategories(params),
    queryFn: () => quotationInvoiceApi.getCustomerCategories(params),
    staleTime: 30_000,
  });
}

export function useCreateQuotationInvoiceCustomerCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: quotationInvoiceApi.createCustomerCategory,
    onSuccess: () => {
      toast.success("ເພີ່ມຫມວດຫມູ່ສຳເລັດ");
      queryClient.invalidateQueries({ queryKey: quotationInvoiceKeys.all });
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof Error ? err.message : "ເພີ່ມຫມວດຫມູ່ລົ້ມເຫຼວ";
      toast.error(msg);
    },
  });
}

export function useUpdateQuotationInvoiceCustomerCategory(id?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      input:
        | Parameters<typeof quotationInvoiceApi.updateCustomerCategory>[1]
        | (Parameters<typeof quotationInvoiceApi.updateCustomerCategory>[1] & {
            id: string;
          }),
    ) => {
      const payload = input as { id?: string } & Parameters<
        typeof quotationInvoiceApi.updateCustomerCategory
      >[1];
      const targetId = payload.id ?? id;
      if (!targetId) throw new Error("Category id is required");
      const { id: _id, ...rest } = payload;
      return quotationInvoiceApi.updateCustomerCategory(targetId, rest);
    },
    onSuccess: () => {
      toast.success("ບັນທຶກຫມວດຫມູ່ສຳເລັດ");
      queryClient.invalidateQueries({ queryKey: quotationInvoiceKeys.all });
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof Error ? err.message : "ບັນທຶກຫມວດຫມູ່ລົ້ມເຫຼວ";
      toast.error(msg);
    },
  });
}

export function useCreateQuotationInvoiceCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: quotationInvoiceApi.createCustomer,
    onSuccess: () => {
      toast.success("ເພີ່ມລູກຄ້າສຳເລັດ");
      queryClient.invalidateQueries({ queryKey: quotationInvoiceKeys.all });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : "ເພີ່ມລູກຄ້າລົ້ມເຫຼວ";
      toast.error(msg);
    },
  });
}

export function useUpdateQuotationInvoiceCustomer(id?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      input:
        | Parameters<typeof quotationInvoiceApi.updateCustomer>[1]
        | (Parameters<typeof quotationInvoiceApi.updateCustomer>[1] & {
            id: string;
          }),
    ) => {
      const payload = input as { id?: string } & Parameters<
        typeof quotationInvoiceApi.updateCustomer
      >[1];
      const targetId = payload.id ?? id;
      if (!targetId) throw new Error("Customer id is required");
      const { id: _id, ...rest } = payload;
      return quotationInvoiceApi.updateCustomer(targetId, rest);
    },
    onSuccess: () => {
      toast.success("ແກ້ໄຂລູກຄ້າສຳເລັດ");
      queryClient.invalidateQueries({ queryKey: quotationInvoiceKeys.all });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : "ແກ້ໄຂລູກຄ້າລົ້ມເຫຼວ";
      toast.error(msg);
    },
  });
}

export function useQuotationInvoiceHistoryQuery(params: HistoryListQuery = {}) {
  return useQuery({
    queryKey: quotationInvoiceKeys.history(params),
    queryFn: () => quotationInvoiceApi.getHistory(params),
  });
}

export function useQuotationInvoiceHistorySummaryQuery() {
  return useQuery({
    queryKey: quotationInvoiceKeys.historySummary(),
    queryFn: () => quotationInvoiceApi.getHistorySummary(),
  });
}

export function useCreateQuotationInvoiceDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDocumentDTO) =>
      quotationInvoiceApi.createDocument(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: quotationInvoiceKeys.all });
      toast.success("ບັນທຶກເອກະສານສຳເລັດແລ້ວ");
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : "ບັນທຶກບໍ່ສຳເລັດ";
      toast.error(msg);
    },
  });
}

export function useUpdateQuotationInvoiceDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CreateDocumentDTO }) =>
      quotationInvoiceApi.updateDocument(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: quotationInvoiceKeys.all });
      toast.success("ແກ້ໄຂເອກະສານສຳເລັດ");
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : "ແກ້ໄຂເອກະສານລົ້ມເຫຼວ");
    },
  });
}

export function useUpdateQuotationInvoiceDocumentStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: string;
      status: "issued" | "cancelled";
    }) => quotationInvoiceApi.updateDocumentStatus(id, status),
    onSuccess: (_, { id, status }) => {
      queryClient.invalidateQueries({ queryKey: quotationInvoiceKeys.all });
      queryClient.invalidateQueries({
        queryKey: quotationInvoiceDetailKeys.detail(id),
      });
      const msg =
        status === "issued"
          ? "ອອກເອກະສານສຳເລັດ"
          : "ຍົກເລີກເອກະສານສຳເລັດ";
      toast.success(msg);
    },
    onError: (err: unknown) => {
      const shortfalls = (
        err as { code?: string; shortfalls?: Array<{ productCode?: string | null; productName?: string | null; shortfall: number }> }
      ).shortfalls;
      if (shortfalls?.length) {
        const detail = shortfalls
          .map(
            (row) =>
              `${row.productCode ?? row.productName ?? "?"} (-${row.shortfall})`,
          )
          .join(", ");
        toast.error(`ສຕັອກບໍ່ພໍ: ${detail}`);
        return;
      }
      toast.error(err instanceof Error ? err.message : "ອັບເດດສະຖານະລົ້ມເຫຼວ");
    },
  });
}

export function useDeleteQuotationInvoiceDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => quotationInvoiceApi.deleteDocument(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: quotationInvoiceKeys.all });
      toast.success("ລຶບເອກະສານສຳເລັດ");
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : "ລຶບເອກະສານລົ້ມເຫຼວ");
    },
  });
}

export function useConvertQuotationToInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: ConvertQuotationToInvoiceBody;
    }) => quotationInvoiceApi.convertToInvoice(id, body),
    onSuccess: (_data, { id: quotationId }) => {
      queryClient.invalidateQueries({ queryKey: quotationInvoiceKeys.all });
      queryClient.invalidateQueries({
        queryKey: quotationInvoiceDetailKeys.detail(quotationId),
      });
      toast.success(
        "ສ້າງໃບບິນງວດແລ້ວ — ກວດແລະແກ້ຍອດງວດໃນຮ່າງກ່ອນອອກເອກະສານ",
      );
    },
    onError: (err: unknown) => {
      toast.error(
        err instanceof Error ? err.message : "ບໍ່ສາມາດຕໍ່ເປັນໃບບິນ",
      );
    },
  });
}

export function useConvertQuotationToSalesOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => quotationInvoiceApi.convertToSalesOrder(id),
    onSuccess: (data, quotationId) => {
      queryClient.invalidateQueries({ queryKey: quotationInvoiceKeys.all });
      queryClient.invalidateQueries({
        queryKey: quotationInvoiceDetailKeys.detail(quotationId),
      });
      toast.success(`ສ້າງຄຳສັ່ງຂາຍ ${data.orderNo} ແລ້ວ`);
    },
    onError: (err: unknown) => {
      toast.error(
        err instanceof Error ? err.message : "ບໍ່ສາມາດແປງເປັນຄຳສັ່ງຂາຍ",
      );
    },
  });
}

export function useConvertInvoiceToReceipt(documentId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, paymentId }: { id: string; paymentId: string }) =>
      quotationInvoiceApi.convertToReceipt(id, paymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: quotationInvoiceKeys.all });
      if (documentId) {
        queryClient.invalidateQueries({
          queryKey: quotationInvoiceDetailKeys.detail(documentId),
        });
      }
      toast.success("ສ້າງໃບຮັບເງິນຈາກໃບບິນແລ້ວ");
    },
    onError: (err: unknown) => {
      toast.error(
        err instanceof Error ? err.message : "ບໍ່ສາມາດຕໍ່ເປັນໃບຮັບເງິນ",
      );
    },
  });
}

export function useCreateDocumentPayment(documentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDocumentPaymentInput) =>
      quotationInvoiceApi.createDocumentPayment(documentId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: quotationInvoiceDetailKeys.detail(documentId),
      });
      queryClient.invalidateQueries({ queryKey: quotationInvoiceKeys.all });
      toast.success("ບັນທຶກການຊຳລະສຳເລັດ");
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : "ບັນທຶກການຊຳລະລົ້ມເຫຼວ");
    },
  });
}

export function useUpdateDocumentPayment(documentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      paymentId,
      input,
    }: {
      paymentId: string;
      input: UpdateDocumentPaymentInput;
    }) => quotationInvoiceApi.updateDocumentPayment(paymentId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: quotationInvoiceDetailKeys.detail(documentId),
      });
      queryClient.invalidateQueries({ queryKey: quotationInvoiceKeys.all });
      toast.success("ແກ້ໄຂການຊຳລະສຳເລັດ");
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : "ແກ້ໄຂການຊຳລະລົ້ມເຫຼວ");
    },
  });
}

export function useDeleteDocumentPayment(documentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paymentId: string) =>
      quotationInvoiceApi.deleteDocumentPayment(paymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: quotationInvoiceDetailKeys.detail(documentId),
      });
      queryClient.invalidateQueries({ queryKey: quotationInvoiceKeys.all });
      toast.success("ລຶບການຊຳລະສຳເລັດ");
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : "ລຶບການຊຳລະລົ້ມເຫຼວ");
    },
  });
}

export function useQiCatalogProductsQuery(params: {
  q?: string;
  categoryId?: string;
  limit?: number;
  offset?: number;
  enabled?: boolean;
}) {
  const { enabled = true, ...query } = params;
  return useQuery({
    queryKey: quotationInvoiceKeys.catalogProducts(query),
    queryFn: () => quotationInvoiceApi.listCatalogProducts(query),
    enabled,
  });
}

export function useQiCatalogRawMaterialsQuery(params: {
  q?: string;
  categoryId?: string;
  limit?: number;
  offset?: number;
  enabled?: boolean;
}) {
  const { enabled = true, ...query } = params;
  return useQuery({
    queryKey: quotationInvoiceKeys.catalogRawMaterials(query),
    queryFn: () => quotationInvoiceApi.listCatalogRawMaterials(query),
    enabled,
  });
}

export function useQiCatalogServicesQuery(params: {
  q?: string;
  categoryId?: string;
  limit?: number;
  offset?: number;
  enabled?: boolean;
}) {
  const { enabled = true, ...query } = params;
  return useQuery({
    queryKey: quotationInvoiceKeys.catalogServices(query),
    queryFn: () => quotationInvoiceApi.listCatalogServices(query),
    enabled,
  });
}

export function useQiCatalogCategoriesQuery() {
  return useQuery({
    queryKey: quotationInvoiceKeys.catalogCategories(),
    queryFn: () => quotationInvoiceApi.listCatalogCategories(),
  });
}

export function useQiHeadOfficeBranchesQuery() {
  return useQuery({
    queryKey: quotationInvoiceKeys.headOfficeBranches(),
    queryFn: () => quotationInvoiceApi.listHeadOfficeBranches(),
  });
}

export function useQiProductAvailabilityQuery(params: {
  productId?: string;
  branchId?: string;
  excludeQuotationId?: string;
  enabled?: boolean;
}) {
  const enabled =
    params.enabled !== false && Boolean(params.productId && params.branchId);
  return useQuery({
    queryKey: quotationInvoiceKeys.productAvailability({
      productId: params.productId ?? "",
      branchId: params.branchId ?? "",
      excludeQuotationId: params.excludeQuotationId,
    }),
    queryFn: () =>
      quotationInvoiceApi.getProductAvailability({
        productId: params.productId!,
        branchId: params.branchId!,
        excludeQuotationId: params.excludeQuotationId,
      }),
    enabled,
  });
}
